import { getPCListWithSeatingPlanData } from "@/api/gql/product-category";
import {
  extractTranslation,
  fetchAndStoreSmeetzPictures,
  filterPrices,
  getSameStockPCs,
  sellOnSoldOut,
} from "@/utils/helpers/product-category";
import {
  getProductCategoryById,
  bulkUpsertProductCategories,
} from "@/dexie/product-category";
import {
  ICardData,
  IPCMembership,
  IProductTableData,
  IProductCategory,
  IProductSlotPrice,
  ITax,
  PCTypes,
  QuestionType,
  SalesEndType,
  SalesStartType,
  IAddToBasketData,
  IPCPromoCodeCampaignList,
  isSeatingParentType,
} from "@/types/db/product-category";
import { FieldLocaleType } from "@/types/app";
import { LogLevel, LogService } from "@/services/logging";
import { getTimeslotById } from "@/dexie/timeslots";
import dayjs from "dayjs";
import { getDeserializedCustomFieldQuestions } from "@/api/custom-fields/custom-fields";
import POSConfigModule from "@/store/modules/pos-config";
import { ITeamSetting } from "@/interfaces/teams/teams";
import { getPcStockUnits } from "../helpers/stock";
import {
  countPssByPricesAndTimeslots,
  getPriceAutomaticAddons,
  getPssAvailability,
} from "./product-slot-state";
import BookingModule from "@/store/modules/booking";
import { ITimeSlot } from "@/types/db/timeslots";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { createPSS } from "../helpers/product-slot-state";
import {
  ICreateAddons,
  IProductSlotState,
  PSSType,
} from "@/types/db/product-slot-state";
import { Currencies } from "@/constants/currency";
import { attachCustomFields } from "./booking";
import { cloneDeep } from "lodash";
import { updateDiscount } from "./discount";
import NotificationService from "@/services/notification.service";
import PSSModule from "@/store/modules/pss";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  createDiscountCodeAccounting,
  createPaymentCodeAccounting,
  hasAppliedGenericDiscountTracking,
  removeAllDiscountCodePendingAccountings,
  removeAllPaymentCodePendingAccountings,
  updateGenericDiscountAccountings,
} from "./new-accountings";
import {
  AccountingState,
  PaymentMethods,
  PromocodeCampaignType,
} from "@/types/db/accounting";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on i18n.global.t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import CommonModule from "@/store/modules/common";
import { recordOperationDuration } from "./common";
import { PSSErrors } from "@/constants/errors/product-slot-state";
import { SmeetzPictureEntityTypes } from "../helpers/pictures";
import { recreateIncertVoucherAccountings } from "@/api/external-vouchers/incert";
import { syncBookingAvailabilities } from "../helpers/booking";

/**
 *
 * @param categoriesIds Fetches PCs from remote DB, convert them and insert them into indexedDB
 * @returns
 */
export async function pullAndStorePCs(categoriesIds: number[]) {
  LogService.info(
    `Going to pull categories from remote db ${categoriesIds.join(" - ")}`
  );
  let pcs = (await getPCListWithSeatingPlanData(categoriesIds)).productCategory;

  // 2- Convert the list of PCs to IProductCategory type and store them to indexedDB

  if (!pcs || !pcs.length) {
    return;
  }

  // consider only not-cancelled pcs
  pcs = pcs.filter((pc) => pc?.cancelled === 0);

  const localPCs = await toLocalProductCategories3(pcs, []);
  await bulkUpsertProductCategories(localPCs);

  // fetch and store all the product logos (pictures) to be printed on the ticket
  await fetchAndStoreSmeetzPictures({
    entityType: SmeetzPictureEntityTypes.ProductLogo,
    entityIds: localPCs.map((pc) => pc.product.id),
  });

  // fetch and store all the ticket (PC) logos (pictures) to be printed on the ticket
  await fetchAndStoreSmeetzPictures({
    entityType: SmeetzPictureEntityTypes.TicketLogo,
    entityIds: localPCs.map((pc) => pc.id),
  });
}

/**
 * Returns products categories fetched from GetTicketGroupsTicketsPCsQuery in local
 * IProductCategory format.
 * Note: Removes duplicates as well.
 * @param productCategories
 * @param retailTaxes is an array of general VATs that will be assigned to retail product
 *
 */
export async function toLocalProductCategories3(
  productCategories: any[],
  taxes?: ITax[]
): Promise<IProductCategory[]> {
  // Transform fetched product categories to local db format IProductCategory
  const pcs: IProductCategory[] = [];
  if (!productCategories?.length) {
    return pcs;
  }

  try {
    const seenIds = new Set<number>();
    for (const pulledPC of productCategories) {
      if (isSeatingParentType(pulledPC.type)) {
        // Don't convert and insert seating parent category in indexedDB
        // Info needed from this seating parent category will be pulled later and attached to its seating child categories
        continue;
      }

      const {
        id,
        name,
        description,
        address,
        type,
        ticketGroups,
        prices,
        product,
        translations,
        qrCodeType,
        multipleScan,
        teamSettingList,
        saleStartType,
        saleStart,
        saleEndType,
        saleEnd,
        salesEndDurationMinutes,
        salesStartDurationDays,
        timeSlotCount,
        seatingParentPSCId,
        resource,
        seatsCat,
      } = pulledPC;

      const categoryProduct = {
        id: product?.id as number,
        isIdivTax: product?.isIdivTax as number,
        name: (product && product.name) || "",
        nameLocale: extractTranslation("name", product?.translations),
        currency: (product && product.currency) || "",
        pictureUrl:
          product && product.pictures
            ? product.pictures.find(
                (p: any) => p && p.picture && p?.picture.isPrimary
              )?.picture?.realUrl || ""
            : "",
        // GETPCListWithSeatingPlanData aliases the branding fields in lower
        // camel case, the raw names are kept as a fallback
        productBrandings: product?.productBrandingList
          ? product?.productBrandingList.map((pb: any) => {
              return {
                id: pb.id ?? pb.Id,
                customText: pb.customText || pb.CustomText || "",
                description1: pb.description1 || pb.Description1 || "",
                description2: pb.description2 || pb.Description2 || "",
                description3: pb.description3 || pb.Description3 || "",
              };
            })
          : [],
        customTicketFields: product.customTicketFieldsList
          .filter(
            (c: any) =>
              // Fifth bit should be always on
              (10000 & Number(c.visibleFor)) === 16 && c.canceled !== 1
          )
          .map((c: any) => {
            return {
              id: c.id,
              answerExample: c.answerExample,
              cancelled: c.canceled,
              informationType: c.informationType,
              list:
                c.questionType === QuestionType.DROPDOWN ||
                c.questionType === QuestionType.LIST ||
                c.questionType === QuestionType.RADIO
                  ? c.list
                  : [],
              name: c.name,
              position: c.position,
              productId: c.productId,
              questionType: c.questionType,
              validation: c.validation,
              visibleFor: c.visibleFor,
              prices: c.prices.map((p: any) => p.priceId),
            };
          }),
        customBookingFields: product.customBookingFieldsList
          .filter(
            (c: any) =>
              // Fifth bit should be always on
              (10000 & Number(c.visibleFor)) === 16 && c.canceled !== 1
          )
          .map((c: any) => {
            return {
              id: c.id,
              answerExample: c.answerExample,
              cancelled: c.canceled,
              informationType: c.informationType,
              list:
                c.questionType === QuestionType.DROPDOWN ||
                c.questionType === QuestionType.LIST ||
                c.questionType === QuestionType.RADIO
                  ? c.list
                  : [],
              name: c.name,
              position: c.position,
              productId: c.productId,
              questionType: c.questionType,
              validation: c.validation,
              visibleFor: c.visibleFor,
            };
          }),
      };

      // we need to show only prices that are meant to be sold on POS
      const pricesToSell = prices?.filter(
        (p: any) =>
          // [16, 17, 18, 20, 21, 22, 23, 24, 31].includes(Number(p?.availability))
          // Fifth bit to the left should be on
          (10000 & Number(p?.availability)) === 16
      );

      const categoryPrices: IProductSlotPrice[] = pricesToSell?.length
        ? // ensure prices compatibility
          (pricesToSell
            .filter((pr: any) => {
              /**
               * filter out prices that are:
               * 1- canceled
               * 2- have retail options but all of them are deleted
               */
              if (pr?.cancelled === 1) {
                return false;
              }
              if (
                pr?.retailOptions &&
                pr.retailOptions.length > 0 &&
                !pr.retailOptions.some((o: any) => !o.deletedAt)
              ) {
                return false;
              }
              return true;
            })
            .map((p: any) => {
              return {
                name: p?.name || "",
                id: p?.id as number,
                nameLocale: extractTranslation("priceName", p?.translations),
                description: p?.description || "",
                descriptionLocale: extractTranslation(
                  "description",
                  p?.translations
                ),
                // If this price is actually a retail then we consider its priceOnsite and use its value everywhere
                value:
                  p?.retailOptions &&
                  p?.retailOptions.length > 0 &&
                  p?.priceOnsite
                    ? p?.priceOnsite
                    : p?.value,
                priceOnsite: !p?.priceOnsite ? 0 : p?.priceOnsite,
                quantity: p?.quantity,
                totalQuantity: p?.totalQuantity,
                // We will not try to assign the field a null value
                ...(p?.vatRateGeneralId
                  ? { vatRateGeneralId: p?.vatRateGeneralId }
                  : null),
                taxes: p?.tax ? [p.tax] : [],
                addons: p.addons,
                updatedAt: p?.updatedAt,
                isEditable: p?.isEditable,
                priceCategory: p?.priceCategory,
                sortPrice: p?.sortPrice,
                retail: p?.retailOptions
                  ?.filter((o: any) => !o?.deletedAt)
                  ?.map((o: any) => {
                    return {
                      id: o?.id,
                      inventoryAlert: o?.inventoryAlert,
                      name: o?.name,
                      nameLocale: JSON.parse(o?.nameLocale) as FieldLocaleType,
                      type: o?.type,
                      barcode: o?.barcode,
                      smeetzPictures: o?.smeetzPictures?.map((s: any) => {
                        return {
                          id: s?.id,
                          name: s?.name,
                          metaData: s?.metaData,
                          // TODO Hamza now s?.entity is of type number | null | undefined even though it is string inside schemas/product-categories.ts and
                          // I ran yarn gql:generate many times without successfully getting it updated ---> Try again running yarn gql:generate
                          entity: String(s?.entity),
                          entityId: s?.entityId,
                        };
                      }),
                    };
                  }),
                packageMinQuantity: p?.packageMinQuantity,
                packageMaxQuantity: p?.packageMaxQuantity,
              };
            }) as IProductSlotPrice[])
        : [];

      // If a given category has no prices, then no need to insert it into the DB
      if (!categoryPrices.length) {
        continue;
      }

      // Extract price IDs for Dexie multi-entry index
      const priceIds = categoryPrices.map((p) => p.id);

      // lets build StockIds arrays
      const stockIds: number[] = [];
      // stock units one ticket consumes per stock (stock_category.quantity)
      const stockConsumptions: { [stockId: number]: number } = {};
      // For FlipLab we allow overbookings
      if (pulledPC.stockCategoryList && pulledPC.stockCategoryList.length) {
        pulledPC.stockCategoryList.forEach((s: any) => {
          if (s && s.stockRead && s.stockRead.id) {
            stockIds.push(s.stockRead.id);
            stockConsumptions[s.stockRead.id] = Number(s.quantity) || 1;
          }
        });

        if (POSConfigModule.isFlipLab) {
          // For FlipLab we allow overbookings
          stockIds.push(-1000);
        }
      }

      const memberships: IPCMembership[] = pulledPC.memberships?.length
        ? pulledPC.memberships.map((m: any) => {
            return {
              id: Number(m?.id),
              startDate: m?.startDate
                ? dayjs.utc(m?.startDate as string).toISOString()
                : "",
              expiryDate: m?.startDate
                ? dayjs.utc(m?.expiryDate as string).toISOString()
                : "",
              type: Number(m?.type),
              expiryType: Number(m?.expiryType),
              periodLength: Number(m?.periodLength),
              numberMandatoryPeriod: Number(m?.numberMandatoryPeriod),
            };
          })
        : [];

      const promoCodeCampaignList: IPCPromoCodeCampaignList[] = pulledPC
        .promoCodeCampaignList?.length
        ? pulledPC.promoCodeCampaignList.map((c: any) => {
            return {
              id: Number(c.id),
              giftCardTicketId: Number(c.giftCardTicketId),
              startDateTime: String(c?.startDateTime),
              endDateTime: String(c?.endDateTime),
              expiryType: Number(c.expiryType),
              expiryDays: Number(c.expiryDays),
              cancelled: Number(c.cancelled),
            };
          })
        : [];

      let categoryTeamSetting: ITeamSetting | null = null;
      if (teamSettingList && teamSettingList[0]) {
        const countedPsp: number[] = JSON.parse(
          `[${teamSettingList[0].countedPsp}]`
        );
        categoryTeamSetting = {
          categoryId: Number(id),
          countedPsp,
          maxParticipants: Number(teamSettingList[0].maxParticipants),
        };
      }

      const pc: IProductCategory = {
        _id: String(id),
        id: id as number,
        name: name || "",
        seatsCat: seatsCat || "",
        description: description || "",
        type: PCTypes[Number(type)],
        address: address ? address : "",
        qrCodeType,
        multipleScan,
        nameLocale: extractTranslation("categoryName", translations),
        descriptionLocale: extractTranslation(
          "categoryDescription",
          translations
        ),
        ticketGroups: ticketGroups?.map(
          (t: any) => t?.ticketGroupId
        ) as number[],
        product: categoryProduct,
        prices: categoryPrices,
        stockIds: stockIds,
        stockConsumptions: stockConsumptions,
        priceIds: priceIds, // Price IDs for Dexie multi-entry index
        memberships: memberships,
        promoCodeCampaignList: promoCodeCampaignList,
        teamSetting: categoryTeamSetting,
        saleStartType,
        saleStart: saleStart ? dayjs.utc(saleStart).toISOString() : saleStart,
        saleEnd: saleEnd ? dayjs.utc(saleEnd).toISOString() : saleStart,
        saleEndType,
        salesEndDurationMinutes,
        salesStartDurationDays,
        timeSlotCount: timeSlotCount,
        isRecurring:
          (timeSlotCount && timeSlotCount > 1) || type === PCTypes.Recurring
            ? true
            : false,
        // Seating Plan logic
        seatingParentPSCId:
          Number(type) === PCTypes.SeatedSub ? seatingParentPSCId : null,
        // At this stage, below seating plan fields will be null until we pull Seating Parent PSC from backend (we will do so if this PSC type=31)
        isParentReccuringSeating: null,
        seatingParentResourceEventKey: null,
        seatingParentCategoryName: null,
        resource:
          resource && resource.quantity
            ? { quantity: resource.quantity }
            : null,
      };

      // Make sure that we aren't adding any ProductCategory that was already added
      if (!seenIds.has(pc.id)) {
        seenIds.add(pc.id);
        pcs.push(pc);
      }
    }

    /////
    // If a PC has custom fields of type DROPDOWN/RADIO/LIST attached to it
    // then we need to pull its deserialized 'list' field
    const isListType = (questionType: number | undefined) =>
      questionType === QuestionType.DROPDOWN ||
      questionType === QuestionType.RADIO ||
      questionType === QuestionType.LIST;

    const customBookingListFields = pcs.flatMap((p) =>
      (p.product.customBookingFields || []).filter((c) =>
        isListType(c?.questionType)
      )
    );
    const customTicketListFields = pcs.flatMap((p) =>
      (p.product.customTicketFields || []).filter((c) =>
        isListType(c?.questionType)
      )
    );

    if (customBookingListFields.length || customTicketListFields.length) {
      // So we have custom fields of type radio/dropdown/list, lets fetch deserialized data

      const customBookingFieldsIds = customBookingListFields.map((c) =>
        Number(c?.id)
      );
      const customTicketFieldsIds = customTicketListFields.map((c) =>
        Number(c?.id)
      );

      const resp = await getDeserializedCustomFieldQuestions(
        Number(POSConfigModule.selectedConfig?.group),
        customTicketFieldsIds,
        customBookingFieldsIds
      );

      // Build Map<productId, PC[]> for O(1) lookups
      const pcsByProductId = new Map<number, IProductCategory[]>();
      for (const pc of pcs) {
        const existing = pcsByProductId.get(pc.product.id);
        if (existing) {
          existing.push(pc);
        } else {
          pcsByProductId.set(pc.product.id, [pc]);
        }
      }

      const deserializedCustomBookingFields = resp.data.customBookingFields;
      const deserializedCustomTicketFields = resp.data.customTicketFields;

      if (deserializedCustomBookingFields?.length) {
        for (const field of deserializedCustomBookingFields) {
          const matchingPCs = pcsByProductId.get(field.productId) || [];
          for (const pc of matchingPCs) {
            const cust = pc.product.customBookingFields?.find(
              (c) => c.id === field.id
            );
            if (cust) {
              cust.list = field.list;
            }
          }
        }
      }

      if (deserializedCustomTicketFields?.length) {
        for (const field of deserializedCustomTicketFields) {
          const matchingPCs = pcsByProductId.get(field.productId) || [];
          for (const pc of matchingPCs) {
            const cust = pc.product.customTicketFields?.find(
              (c) => c.id === field.id
            );
            if (cust) {
              cust.list = field.list;
            }
          }
        }
      }
    }
    /////
  } catch (error) {
    LogService.info(
      `Error in toLocalProductCategories3`,
      error,
      LogLevel.Error
    );
  }
  return pcs;
}

/**
 * Returns whether we're in the sales period of the ticket or no
 */
// TODO HAMZA: In order to finish the logic below we'll need ActivityStartDateTime and ActivityEndDateTime
// with PC. But the issue is we don't have it in IPSC. The only place to get it is to sort timeslots
// and take the first timeslot and the last timeslot but this will cost a lot because this function is
// run with every card and getting timeslots data will need indexedDB connection and sorting is also expensive
// think about a way to:
// for each Ticket PC (not retail), sort slots and take first and last slot data and store them in new fields pc.activityStart and pc.activityEnd
// but this last point should be done based on a setting, bcz it is expensive to do it and may be the organizer will not even care about it
export function isSalesPeriod(
  /*pc: IProductCategory,
  activityStart: string,
  activityEnd: string*/
  productData: ICardData | IProductTableData
) {
  // if this is flipLab then we hard code the settings to be as below:
  // sales start now and end when the activity start
  if (
    POSConfigModule.isFlipLab &&
    productData.type !== PCTypes[PCTypes.GiftCard] &&
    productData.type !== PCTypes[PCTypes.MembershipVoucher] &&
    productData.type !== PCTypes[PCTypes.MembershipSeated] &&
    productData.type !== PCTypes[PCTypes.Membership]
  ) {
    // this is flipLab activitiy, hardcoding settings
    productData.saleStartType = SalesStartType.SALE_START_TYPE_NOW;
    productData.saleEndType = SalesEndType.SALE_END_TYPE_START;
  }

  if (productData.type === PCTypes[PCTypes.Retail]) {
    return true;
  }

  const activityStart = productData.startDateTime;
  const activityEnd = productData.endDateTime;

  if (!activityStart || !activityEnd) {
    return true;
  }

  // Release only for KoJump Brétigny first OR FlipLab groups
  if (
    (POSConfigModule.groupId &&
      [
        1, 16342 /*DEV*/, 16383 /*TEST*/, 19549 /*KoJump Brétigny*/,
        1 /*Smeetz*/,
      ].includes(POSConfigModule.groupId)) ||
    POSConfigModule.isFlipLab
  ) {
    // We return true only if sales has started and hasn't ended yet
    let isSalesStarted = true;
    let isSalesEnded = false;

    // lets check if sales has started
    switch (productData.saleStartType) {
      case SalesStartType.SALE_START_TYPE_NOW:
        isSalesStarted = true;
        break;
      case SalesStartType.SALE_START_TYPE_DATE:
        // if saleStart is in the past already then it is true
        // dayjs() is the device dateTime of now, not utc
        // cardData.saleStart is stored in indexedDB in utc
        // dayjs(cardData.saleStart) converted to device zone
        if (dayjs(productData.saleStart).isBefore(dayjs())) {
          isSalesStarted = true;
        } else {
          isSalesStarted = false;
        }
        break;
      case SalesStartType.SALE_START_TYPE_OFFSET:
        // if X days before activity starts is in the past already, then isSalesStarted is true
        if (
          productData.salesStartDurationDays &&
          dayjs(activityStart)
            .add(-Number(productData.salesStartDurationDays), "day")
            .isBefore(dayjs())
        ) {
          isSalesStarted = true;
        } else {
          isSalesStarted = false;
        }
        break;
      default:
        // we shouldn't stop sales (default behaviour of the app) if none of the above is true
        // (in case we have a setting issue)
        isSalesStarted = true;
    }

    // lets check if sales has ended
    switch (productData.saleEndType) {
      case SalesEndType.SALE_END_TYPE_END:
        // if activity endDate is now in the past then sales has ended
        if (dayjs(activityEnd).isBefore(dayjs())) {
          isSalesEnded = true;
        } else {
          isSalesEnded = false;
        }
        break;
      case SalesEndType.SALE_END_TYPE_START:
        // if activity startDate is now in the past then sales has ended
        if (dayjs(activityStart).isBefore(dayjs())) {
          isSalesEnded = true;
        } else {
          isSalesEnded = false;
        }
        break;
      case SalesEndType.SALE_END_TYPE_DATE:
        // if saleEnd is in the past then sales has ended
        // dayjs() is the device dateTime of now, not utc
        // cardData.saleStart is stored in indexedDB in utc
        // dayjs(cardData.saleStart) converted to device zone
        if (dayjs(productData.saleEnd).isBefore(dayjs())) {
          isSalesEnded = true;
        } else {
          isSalesEnded = false;
        }
        break;
      case SalesEndType.SALE_END_TYPE_OFFSET:
        if (
          productData.salesEndDurationMinutes &&
          Number(productData.salesEndDurationMinutes)
        ) {
          if (Number(productData.salesEndDurationMinutes) >= 0) {
            // if salesEndDurationMinutes is POSITIVE then sales end salesEndDurationMinutes minutes BEFORE activity starts

            // if activity startDate minus salesEndDurationMinutes is in the past then sales has ended
            if (
              dayjs(activityStart)
                .add(-1 * Number(productData.salesEndDurationMinutes), "minute")
                .isBefore(dayjs())
            ) {
              isSalesEnded = true;
            } else {
              isSalesEnded = false;
            }
          } else {
            // if salesEndDurationMinutes is NEGATIVE then sales end salesEndDurationMinutes minutes AFTER activity starts

            // if activity startDate plus absolute value of salesEndDurationMinutes is in the past then sales have ended
            if (
              dayjs(activityStart)
                .add(
                  Math.abs(Number(productData.salesEndDurationMinutes)),
                  "minute"
                )
                .isBefore(dayjs())
            ) {
              isSalesEnded = true;
            } else {
              isSalesEnded = false;
            }
          }
        } else {
          // if there is an issue with settings then we don't block sales
          isSalesEnded = false;
        }
        break;
      default:
        // we shouldn't stop sales (default behaviour of the app) if none of the above is true
        // (in case we have a setting issue)
        isSalesEnded = false;
    }
    if (
      (POSConfigModule.groupId &&
        [16342 /*TEST*/, 1 /*Smeetz*/].includes(POSConfigModule.groupId)) ||
      POSConfigModule.isFlipLab
    ) {
      // if it is FlipLab and the slot starts today then we hardcode it to be: sales haven't ended yet
      // so that they keep selling the slot even if it is in the past (but today)
      const yesterday = dayjs().add(-1, "day");
      const tomorrow = dayjs().add(1, "day");
      if (
        dayjs(productData.startDateTime).isBefore(tomorrow) &&
        dayjs(productData.startDateTime).isAfter(yesterday)
      ) {
        // So the slot startss today
        isSalesEnded = false;
      }
    }
    return isSalesStarted && !isSalesEnded;
  }
  return true;
}

export function isProductAvailable(product: ICardData | IProductTableData) {
  const productCategoryId = product.productCategoryId;
  const ownStockUnits = getPcStockUnits(product.stockConsumptions);
  let bookedAlready = 0;
  if (!product.timeslot_ID) {
    bookedAlready = countPssByPricesAndTimeslots(
      product.prices || [],
      Number(productCategoryId),
      undefined,
      ownStockUnits
    );
  } else {
    bookedAlready = countPssByPricesAndTimeslots(
      product.prices || [],
      Number(productCategoryId),
      {
        timeslotId: product.timeslot_ID,
        startDateTime: String(product.startDateTime),
        endDateTime: String(product.endDateTime),
      },
      ownStockUnits
    );
  }
  return Number(product.dbAvailability) - bookedAlready;
}

export function isSharedStocksWithOverbooking(
  product: ICardData | IProductTableData
) {
  return (
    POSConfigModule.isFlipLab &&
    product.stockIds &&
    product.stockIds.length &&
    product.stockIds.includes(-1000)
  );
}

export async function selectPC(
  product: ICardData | IProductTableData,
  isRetail: boolean
): Promise<
  | { pssQuantity?: number; pc: IProductCategory; timeslot?: ITimeSlot }
  | undefined
> {
  const productCategoryId = Number(product.productCategoryId);
  if (!productCategoryId) {
    LogService.info(
      `Tapped card has no resolvable category`,
      { groupId: POSConfigModule.groupId },
      LogLevel.Error
    );
    NotificationService.showGeneralResponseError();
    return;
  }
  let selectedPC: IProductCategory | undefined;
  try {
    selectedPC = await getProductCategoryById(productCategoryId);
  } catch (error) {
    NotificationService.showGeneralResponseError();
    LogService.info("Error fetching selected PC", { error });
  }

  if (!selectedPC) {
    return;
  }

  // only plain tickets are bound to a slot
  const isNormalTicket = selectedPC.type === PCTypes[PCTypes.Normal];
  let timeslot: ITimeSlot | undefined;
  if (!isRetail && product.timeslot_ID) {
    timeslot = await getTimeslotById(Number(product.timeslot_ID));
  }
  if (!isRetail && isNormalTicket && !timeslot) {
    LogService.info(
      `Timeslot of the tapped ticket not found, blocking the sale`,
      {
        groupId: POSConfigModule.groupId,
        productCategoryId,
        timeslotId: product.timeslot_ID || null,
      },
      LogLevel.Error
    );
    NotificationService.showGeneralResponseError();
    return;
  }
  const pc = filterPrices(selectedPC);

  const calcValue = Number(BookingModule.calculatorValue);

  if (!product.prices) return { pc, timeslot };
  const nbPrices = product.prices.length;

  if (nbPrices && nbPrices === 1 && calcValue === 0) {
    return { pssQuantity: nbPrices, pc, timeslot };
  } else if (nbPrices && nbPrices === 1 && calcValue > 0) {
    BookingModule.setCalcValue("0");
    return { pssQuantity: calcValue, pc, timeslot };
  } else {
    return { pc, timeslot };
  }
}

export function allowSharedStocksOverBooking(
  product: ICardData | IProductCategory
) {
  return (
    POSConfigModule.isFlipLab &&
    product.stockIds &&
    product.stockIds.length &&
    product.stockIds.includes(-1000)
  );
}

export async function addToBasket(data: IAddToBasketData) {
  const operationStartTime = performance.now();

  let ts: ITimeSlot | undefined = undefined;
  if (!data.isRetail) {
    ts = data.timeslot;
    // only plain tickets are bound to a slot
    const isNormalTicket =
      data.productCategory.type === PCTypes[PCTypes.Normal];
    if (isNormalTicket && !ts) {
      LogService.info(
        `Add to basket without a timeslot, blocking the ticket`,
        {
          groupId: POSConfigModule.groupId,
          categoryId: data.productCategory?.id,
        },
        LogLevel.Error
      );
      NotificationService.showCustomError(t("error.ticket-slot-mismatch"));
      return;
    }
    if (isNormalTicket && ts && ts.categoryId !== data.productCategory.id) {
      LogService.info(
        `Add to basket blocked: timeslot belongs to another category`,
        {
          groupId: POSConfigModule.groupId,
          pssCategoryId: data.productCategory.id,
          tsCategoryId: ts.categoryId,
          tsId: ts.id,
        },
        LogLevel.Error
      );
      NotificationService.showCustomError(t("error.ticket-slot-mismatch"));
      return;
    }
  }

  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  if (!data.selectedProduct.prices?.length) return;
  const price = data.price || data.selectedProduct.prices[0];
  const priceId = price.id;
  const priceValue = price.value;

  const currentPSSCount = booking.pssList.length;
  const bookingUUID = booking.uuid as string;

  // let get the addons related to this product price
  let pssAddons: ICreateAddons[] = [];
  try {
    pssAddons = await getPriceAutomaticAddons(
      price,
      booking,
      ts?.startDateTime,
      String(ts?.id)
    );
  } catch (error) {
    if (error?.message === PSSErrors.UnavailableMandatoryAddon) {
      // a custom error is thrown in case of unavailable mandatory addons
      const unavailableAddon = error.addon;
      const msg = t("error.unavailable-addon").replace("XXX", unavailableAddon);
      NotificationService.showCustomError(msg);
      return;
    }
  } // check the availability of the addons
  // Only a MANDATORY unavailable addon blocks the sale;
  // an unavailable optional addon is skipped with a warning notification.
  let canBook = true;
  const availableAddons: ICreateAddons[] = [];
  for (const addon of pssAddons) {
    const availability = getPssAvailability(addon);
    const isUnavailable =
      availability < 1 &&
      !sellOnSoldOut(
        addon.productCategory.prices,
        addon.productCategory.type
      ) &&
      !allowSharedStocksOverBooking(data.selectedProduct);
    if (!isUnavailable) {
      availableAddons.push(addon);
      continue;
    }
    if (addon.isMandatory) {
      canBook = false;
      const msg = t("availability.autumatic-addon")
        .replace("X1", addon.productCategory.name)
        .replace("X2", String(availability));
      NotificationService.showCustomError(msg);
    } else {
      const msg = t("availability.optional-addon-skipped", {
        addonName: addon.productCategory.name,
      });
      NotificationService.showCustomMessage(msg, "warning");
    }
  }
  if (!canBook) {
    return;
  }
  pssAddons = availableAddons;

  // if this pss have addons let's get the last endDateTime to be in the qrcode token
  let tokenEndDate: string | null = ts?.endDateTime || null;
  for (const addon of pssAddons) {
    if (!addon.timeslot) continue;
    if (
      !tokenEndDate ||
      (tokenEndDate && dayjs(addon.timeslot.endDateTime).isAfter(tokenEndDate))
    ) {
      tokenEndDate = addon.timeslot.endDateTime;
    }
  }

  if (data.nonAutomaticAddons) {
    data.nonAutomaticAddons.forEach((addon) => {
      pssAddons.push(addon);
    });
  }

  let pssList: IProductSlotState[] = [];
  try {
    // create pss
    pssList = await createPSS({
      pssType: data.isRetail
        ? PSSType[PSSType.Retail]
        : PSSType[PSSType.Ticket],
      bookingUuid: bookingUUID,
      priceId,
      categoryId: data.productCategory.id,
      priceValue: priceValue,
      timeslot: ts,
      productCategory: data.productCategory,
      count: data.pssQuantity,
      // create booking will throw if we have no seller id
      sellerId: booking.sellerId as number,
      addons: pssAddons,
      sendTicket: 1, //normal pss
      token: null, //it will be set later in createPSS
      mainSlot: null,
      packagePriceId: null,
      tokenEndDate,
      bookingReference: booking.reference,
      seatId: data.seatId, // we know in seating plan case we create one PSS at a time, this pss should have a seatId
      eventKey: data.eventKey,
      seatType: data.seatType,
      parentSeatingType: data.parentSeatingType,
    });
  } catch (error) {
    LogService.info(`Error when creating PSS for booking ${bookingUUID}`, {
      data,
      error,
    });
  }

  // add the created pss to the current booking
  BookingModule.addPSS({
    bookingUUID,
    pssList,
  });

  // For each pss created lets add its extension.
  // Each pss must reflect availability only onto the PCs that share ITS OWN
  // stock entity. Computing one combined same-stock set for the whole pssList
  // would make a main ticket claim the addon's stock PCs (and vice versa),
  // double-counting availability for overlapping timeslots (L3-7768).
  const sameStockPCsByCategory = new Map<number, number[]>();
  for (const pss of pssList) {
    if (!sameStockPCsByCategory.has(pss.categoryId)) {
      const pcs = await getSameStockPCs([String(pss.categoryId)]);
      sameStockPCsByCategory.set(
        pss.categoryId,
        pcs && pcs.length ? pcs.map((pc) => pc.id) : []
      );
    }
  }
  for (const pss of pssList) {
    let psc = data.productCategory;
    if (pss.categoryId !== data.productCategory.id) {
      // Add-on pss (automatic or manually selected) belong to their own
      // product category. The extension must be built from that PC, since
      // its ticketGroups drive promo/sale-collection eligibility and its
      // prices carry the taxes of the add-on line.
      const addonPC = await getProductCategoryById(pss.categoryId);
      if (addonPC) {
        psc = addonPC;
      }
    }
    const sameStockPCsIds = sameStockPCsByCategory.get(pss.categoryId) || [];
    if (sameStockPCsIds.length) {
      PSSModule.addPSSExtensions([{ psc, pss, sameStockPCs: sameStockPCsIds }]);
    } else {
      PSSModule.addPSSExtensions([{ psc, pss }]);
    }
  }

  // If this the first PC of this booking then update the booking currency
  if (currentPSSCount === 0) {
    const updatedBooking = Object.assign({}, booking);
    const key = data.productCategory.product
      .currency as keyof typeof Currencies;
    updatedBooking.currency = Currencies[key];
    BookingModule.updateBooking(updatedBooking);
  }
  // Add custom fields if necessary
  if (
    (data.productCategory.product.customBookingFields &&
      data.productCategory.product.customBookingFields.length) ||
    (data.productCategory.product.customTicketFields &&
      data.productCategory.product.customTicketFields.length)
  ) {
    LogService.info(
      `So PC has custom fields, attaching custom fields to the booking and/or pss for booking ${bookingUUID}`
    );
    attachCustomFields({
      pc: data.productCategory,
      pssList: pssList.map((pss) => {
        return {
          priceId: pss.priceId,
          pssUuid: String(pss.uuid),
          priceName: pss.priceName,
          categoryId: pss.categoryId,
          categoryName: pss.categoryName,
        };
      }),
    });
  }

  // DiscountTracking update
  await updateDiscount(priceId, pssList);

  // New Logic of creating genericDiscount/promocodeDiscounts/paymentCodes accounting
  // TODO Hamza: we always update created acountings of genericDiscount/promocodeDiscunt/paymentCode here
  const hasGenericDiscount = hasAppliedGenericDiscountTracking.value;
  let hasPendingGenericDiscountAccounting = false;
  const genericDiscountAccIndex = (
    BookingModule.selectedBooking as IProductSlotBooking
  ).accountings.findIndex(
    (acc) =>
      acc.state === AccountingState.Pending &&
      !acc.refund &&
      acc.paymentMethod === PaymentMethods.METHODS_POS_DISCOUNT
  );
  if (genericDiscountAccIndex > -1) {
    hasPendingGenericDiscountAccounting = true;
  }

  if (hasGenericDiscount || hasPendingGenericDiscountAccounting) {
    const bookingCopy = cloneDeep(
      BookingModule.selectedBooking as IProductSlotBooking
    );
    /////////////////
    // TODO Hamza: If we have applied codes for this booking, then we remove their accountings
    //     - Remove pending payment code accountings of all applied payment codes
    removeAllPaymentCodePendingAccountings(bookingCopy);
    //     - Remove pending discount code accountings of all applied discount codes
    removeAllDiscountCodePendingAccountings(bookingCopy);
    // Now lets update the booking in the state before appyling the discount, and before applying any code
    // BookingModule.updateBooking(bookingCopy);
    /////////////////
    updateGenericDiscountAccountings(bookingCopy);
    BookingModule.updateBooking(bookingCopy);

    // Reusing bookingCopy for recreating codes accountings will not push restToPay to react
    const bookingCopy1 = cloneDeep(
      BookingModule.selectedBooking as IProductSlotBooking
    );
    /////////////////
    // TODO Hamza: Recreate accountings for applied discount codes and then payment codes
    //     - Create discount code accountings of all applied discount codes
    // lets get all applied discount codes and sort them by applyDateTime
    const sortedAppliedDiscountCodes = BookingModule.appliedPromocodes.filter(
      (p) =>
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_VOUCHER &&
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_GIFT_CARD &&
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
    ); /*.sort((p) => p.createdAt)*/
    for (let i = 0; i < sortedAppliedDiscountCodes.length; i++) {
      createDiscountCodeAccounting(bookingCopy1, sortedAppliedDiscountCodes[i]);
    }
    // BookingModule.updateBooking(bookingCopy);
    //     - Create payment code accountings of all applied payment codes
    // lets get all applied payment codes and sort them by applyDateTime
    const sortedAppliedPaymentCodes = BookingModule.appliedPromocodes.filter(
      (p) =>
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER ||
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD ||
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
    ); /*.sort((p) => p.createdAt)*/
    for (let i = 0; i < sortedAppliedPaymentCodes.length; i++) {
      createPaymentCodeAccounting(bookingCopy1, sortedAppliedPaymentCodes[i]);
    }
    BookingModule.updateBooking(bookingCopy1);
    /////////////////
  } else {
    // There is no generic discount applied
    // We remove any discount/payment codes accounting that were generated by an applied code
    // and then we recreate them
    const bookingCopy = cloneDeep(
      BookingModule.selectedBooking as IProductSlotBooking
    );
    //     - Remove pending payment code accountings of all applied payment codes
    removeAllPaymentCodePendingAccountings(bookingCopy);
    //     - Remove pending discount code accountings of all applied discount codes
    removeAllDiscountCodePendingAccountings(bookingCopy);
    BookingModule.updateBooking(bookingCopy);

    // Reusing bookingCopy for recreating codes accountings will not push restToPay to react
    const bookingCopy1 = cloneDeep(
      BookingModule.selectedBooking as IProductSlotBooking
    );
    /////////////////
    // TODO Hamza: Recreate accountings for applied discount codes and then payment codes
    //     - Create discount code accountings of all applied discount codes
    // lets get all applied discount codes and sort them by applyDateTime
    const sortedAppliedDiscountCodes = BookingModule.appliedPromocodes.filter(
      (p) =>
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_VOUCHER &&
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_GIFT_CARD &&
        p.promocode.promoCodeType !== PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
    ); /*.sort((p) => p.createdAt)*/
    for (let i = 0; i < sortedAppliedDiscountCodes.length; i++) {
      createDiscountCodeAccounting(bookingCopy1, sortedAppliedDiscountCodes[i]);
    }
    // BookingModule.updateBooking(bookingCopy);
    //     - Create payment code accountings of all applied payment codes
    // lets get all applied payment codes and sort them by applyDateTime
    const sortedAppliedPaymentCodes = BookingModule.appliedPromocodes.filter(
      (p) =>
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER ||
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD ||
        p.promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
    ); /*.sort((p) => p.createdAt)*/
    for (let i = 0; i < sortedAppliedPaymentCodes.length; i++) {
      createPaymentCodeAccounting(bookingCopy1, sortedAppliedPaymentCodes[i]);
    }
    BookingModule.updateBooking(bookingCopy1);
  }

  // Now lets recreate Incert voucher accountings
  recreateIncertVoucherAccountings();

  // meausre addToBasket performance
  const operationEndTime = performance.now();
  const operationDuration = operationEndTime - operationStartTime;
  recordOperationDuration("add_to_basket", operationDuration);

  // Check booking availabilities
  syncBookingAvailabilities(booking);
}
